import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Button, Checkbox, Col, InputNumber, Modal, Popconfirm, Row, Space,
  Switch, Table, Tag, TimePicker, Tooltip, message,
} from "antd";
import moment from "moment";
import Water from "../../../api/Water";

// 水情警報設定（function_11 的警報欄位）。
//
// 判斷在 .74 的 water 服務存結果時進行（以校正後數值比對），發送由 .45 的
// WaterAlert 負責。這個視窗只管設定與檢視紀錄：
//   * 三項 × 三級門檻，每格可留空（該級不判斷）
//   * 一級最嚴重；數值 >= 門檻即達該級，所以有填的級數必須 一級 > 二級 > 三級
//   * 水位沿用既有的 water_level_*_height，水文歷線圖的警戒線也讀這三欄
//   * 發報規則（時段、星期、冷卻、改送 Buffer）與 gas 機具相同

const LEVELS = [
  { n: 1, text: "一級", color: "#d9363e", hint: "最嚴重" },
  { n: 2, text: "二級", color: "#f06b4e" },
  { n: 3, text: "三級", color: "#f0ad4e" },
];

const METRICS = [
  { key: "water_level", param: "parameter1", unit: "unit1", name: "水位", defUnit: "m",
    cols: ["water_level_one_height", "water_level_two_height", "water_level_three_height"] },
  { key: "average_velocity", param: "parameter2", unit: "unit2", name: "流速", defUnit: "m/s",
    cols: ["velocity_level_one", "velocity_level_two", "velocity_level_three"] },
  { key: "discharge", param: "parameter3", unit: "unit3", name: "流量", defUnit: "cms",
    cols: ["discharge_level_one", "discharge_level_two", "discharge_level_three"] },
];

// bit 順序對應 Python datetime.weekday()：週一 = bit0 … 週日 = bit6
const WEEKDAYS = ["一", "二", "三", "四", "五", "六", "日"]
  .map((text, index) => ({ label: `週${text}`, value: index }));

const STATUS = {
  pending: { text: "等待發送", color: "processing" },
  sending: { text: "發送中", color: "processing" },
  sent: { text: "已發送", color: "success" },
  suppressed: { text: "冷卻中", color: "warning" },
  muted: { text: "時段外", color: "default" },
  failed: { text: "失敗", color: "error" },
};

const label = (value, fallback) => (value && value !== "--" ? value : fallback);
const maskToDays = (mask) => WEEKDAYS.filter((d) => mask & (1 << d.value)).map((d) => d.value);
const daysToMask = (days) => days.reduce((mask, d) => mask | (1 << d), 0);
const isSet = (v) => v !== null && v !== undefined && v !== "";

function orderErrors(values) {
  const errors = {};
  METRICS.forEach((metric) => {
    const v = metric.cols.map((col) => values[col]);
    for (let i = 0; i < 3; i += 1) {
      for (let j = i + 1; j < 3; j += 1) {
        if (isSet(v[i]) && isSet(v[j]) && !(Number(v[i]) > Number(v[j]))) {
          errors[metric.key] = "須符合 一級 > 二級 > 三級";
        }
      }
    }
  });
  return errors;
}

function LevelTag({ level }) {
  const info = LEVELS.find((l) => l.n === level);
  if (!info) return <Tag>未達警戒</Tag>;
  return <Tag color={info.color}>{info.text}</Tag>;
}

export default function WaterAlertModal({
  machineID, station, open, canEdit, onClose, onSaved,
}) {
  const [values, setValues] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    const [config, history] = await Promise.all([
      Water.getAlert(machineID),
      Water.getAlertLog(machineID, 20),
    ]);
    if (config.ok) {
      setValues(config.data);
      setLoadError(null);
    } else {
      setLoadError(config.error);
    }
    if (history.ok) setLog(history.data);
  }, [machineID]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const set = (patch) => setValues((prev) => ({ ...prev, ...patch }));
  const errors = values ? orderErrors(values) : {};
  const hasErrors = Object.keys(errors).length > 0;

  const metricName = (metric) => label(station?.[metric.param], metric.name);
  const metricUnit = (metric) => label(station?.[metric.unit], metric.defUnit);

  const save = async () => {
    const payload = {};
    METRICS.forEach((metric) => metric.cols.forEach((col) => {
      payload[col] = isSet(values[col]) ? Number(values[col]) : null;
    }));
    Object.assign(payload, {
      alert_enabled: values.alert_enabled ? 1 : 0,
      send_to_buffer: values.send_to_buffer ? 1 : 0,
      alert_cooldown_min: values.alert_cooldown_min,
      alert_weekdays: values.alert_weekdays,
      alert_time_start: values.alert_time_start,
      alert_time_end: values.alert_time_end,
    });
    setSaving(true);
    const result = await Water.updateAlert(machineID, payload);
    setSaving(false);
    if (!result.ok) {
      message.error(result.error + (result.details ? `：${result.details.join("；")}` : ""));
      return;
    }
    message.success("已儲存警報設定");
    if (onSaved) onSaved();
    load();
  };

  const resetState = async () => {
    const result = await Water.resetAlertState(machineID);
    if (result.ok) {
      message.success("已重設，下一筆達警戒的結果會視為新觸發");
      load();
    } else {
      message.error(`重設失敗：${result.error}`);
    }
  };

  const stateMetrics = values?.alert_state?.metrics || {};
  const notifiedAt = values?.alert_state?.notified_at;

  const logColumns = [
    {
      title: "時間", dataIndex: "scheduled_at", width: 110,
      render: (value) => new Date(value).toLocaleString([], {
        month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }),
    },
    {
      title: "狀態", dataIndex: "alert_status", width: 90,
      render: (value) => {
        const status = STATUS[value] || { text: value, color: "default" };
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: "等級", width: 220,
      render: (_, row) => {
        const metrics = row.alert_levels?.metrics || {};
        const tags = METRICS.filter((m) => metrics[m.key]?.configured).map((m) => {
          const level = metrics[m.key].level;
          if (level === null || level === undefined) {
            return <Tag key={m.key}>{metricName(m)} 偵測失敗</Tag>;
          }
          if (!level) return null;
          const info = LEVELS.find((l) => l.n === level);
          return <Tag key={m.key} color={info.color}>{metricName(m)} {info.text}</Tag>;
        }).filter(Boolean);
        return tags.length ? tags : <span style={{ color: "var(--secondary-text)" }}>未達警戒</span>;
      },
    },
    { title: "說明", dataIndex: "alert_error", ellipsis: true },
  ];

  const footer = [
    <Button key="close" onClick={onClose}>關閉</Button>,
    canEdit && (
      <Button key="save" type="primary" loading={saving}
              disabled={!values || hasErrors} onClick={save}>
        儲存
      </Button>
    ),
  ].filter(Boolean);

  return (
    <Modal title="警報設定" open={open} onCancel={onClose} width={880}
           footer={footer} destroyOnClose>
      {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 12 }} />}
      {!values && !loadError && <div style={{ padding: 24, textAlign: "center" }}>載入中…</div>}

      {values && (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
                 message="以校正後的數值比對門檻，數值 ≥ 門檻即達該級，一級最嚴重。留空表示該級不判斷。" />

          {/* ---------- 門檻 ---------- */}
          <Row gutter={8} style={{ fontWeight: 500, marginBottom: 6 }}>
            <Col span={6}>項目</Col>
            {LEVELS.map((level) => (
              <Col span={6} key={level.n}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4,
                               background: level.color, marginRight: 6 }} />
                {level.text}{level.hint ? `（${level.hint}）` : ""}
              </Col>
            ))}
          </Row>
          {METRICS.map((metric) => (
            <div key={metric.key} style={{ marginBottom: 8 }}>
              <Row gutter={8} align="middle">
                <Col span={6}>
                  {metricName(metric)}
                  <span style={{ color: "var(--secondary-text)", marginLeft: 4 }}>
                    ({metricUnit(metric)})
                  </span>
                </Col>
                {metric.cols.map((col) => (
                  <Col span={6} key={col}>
                    <InputNumber style={{ width: "100%" }} placeholder="未設定"
                                 disabled={!canEdit} step={0.1}
                                 status={errors[metric.key] ? "error" : undefined}
                                 value={values[col]}
                                 onChange={(v) => set({ [col]: v })} />
                  </Col>
                ))}
              </Row>
              {errors[metric.key] && (
                <div style={{ color: "#d9363e", fontSize: 12, marginLeft: "25%" }}>
                  {metricName(metric)}{errors[metric.key]}
                </div>
              )}
            </div>
          ))}

          {/* ---------- 發報規則 ---------- */}
          <h6 style={{ marginTop: 16 }}>發報規則</h6>
          <Row gutter={[16, 10]} align="middle">
            <Col span={12}>
              <Space>
                <Switch checked={!!values.alert_enabled} disabled={!canEdit}
                        onChange={(v) => set({ alert_enabled: v ? 1 : 0 })} />
                啟用通知
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Tooltip title="開啟時警報只送到內部 TG Buffer 群，現場群組不會收到。測試期間建議保持開啟。">
                  <Switch checked={!!values.send_to_buffer} disabled={!canEdit}
                          onChange={(v) => set({ send_to_buffer: v ? 1 : 0 })} />
                </Tooltip>
                改送 Buffer 群
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                同等級重發間隔
                <InputNumber min={1} max={1440} disabled={!canEdit}
                             value={values.alert_cooldown_min}
                             onChange={(v) => set({ alert_cooldown_min: v })} />
                分鐘
              </Space>
              <div style={{ fontSize: 11, color: "var(--secondary-text)" }}>
                升級、降級、解除一律立即發送，不受此限
              </div>
            </Col>
            <Col span={12}>
              <Space>
                發報時段
                <TimePicker.RangePicker
                  format="HH:mm" order={false} allowClear={false} disabled={!canEdit}
                  value={[moment(values.alert_time_start, "HH:mm"),
                          moment(values.alert_time_end, "HH:mm")]}
                  onChange={(range) => range && set({
                    alert_time_start: range[0].format("HH:mm"),
                    alert_time_end: range[1].format("HH:mm"),
                  })} />
              </Space>
              <div style={{ fontSize: 11, color: "var(--secondary-text)" }}>
                起始晚於結束視為跨午夜（例如 20:00 ~ 06:00）；時段外不發報也不佔冷卻
              </div>
            </Col>
            <Col span={24}>
              <Checkbox.Group options={WEEKDAYS} disabled={!canEdit}
                              value={maskToDays(values.alert_weekdays ?? 127)}
                              onChange={(days) => set({ alert_weekdays: daysToMask(days) })} />
            </Col>
          </Row>

          {/* ---------- 目前狀態 ---------- */}
          <h6 style={{ marginTop: 16 }}>
            目前狀態
            {notifiedAt && (
              <span style={{ fontSize: 12, fontWeight: 400, color: "var(--secondary-text)",
                             marginLeft: 8 }}>
                上次通知 {new Date(notifiedAt).toLocaleString([], {
                  month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </h6>
          <Space wrap>
            {METRICS.map((metric) => (
              <span key={metric.key}>
                {metricName(metric)}：<LevelTag level={stateMetrics[metric.key]?.level} />
              </span>
            ))}
            {canEdit && (
              <Popconfirm title="清除狀態後，下一筆達警戒的結果會視為新觸發並立即發送。確定？"
                          okText="重設" cancelText="取消" onConfirm={resetState}>
                <Button size="small">重設狀態</Button>
              </Popconfirm>
            )}
          </Space>

          {/* ---------- 紀錄 ---------- */}
          <h6 style={{ marginTop: 16 }}>發報紀錄</h6>
          <Table size="small" rowKey="id" columns={logColumns} dataSource={log}
                 pagination={{ pageSize: 6, size: "small" }} />
        </>
      )}
    </Modal>
  );
}